import * as THREE from 'three/webgpu';
import { SceneAuxiliaryObjectRegistry } from '../scene-object';
import type {
    SectionViewCapRenderStageSource,
    SectionViewRenderPort,
} from '../../types/section-view/SectionViewTypes';
import {
    markCadObjectAsRuntimeContainer,
    markCadObjectAsRuntimeDerived,
} from '../document/CadObjectPersistence';
import { isFeatureFace } from '../mesh/feature/FeatureFace';
import type { SectionPlane } from './SectionPlane';

/** SOLIDWORKS 平面剖允许的最大同时剖切数。 */
const MAXIMUM_SECTION_PLANE_COUNT = 3;
/** 空模型仍需可见的最小预览边长，单位为米。 */
const MINIMUM_PLANE_VISUAL_SIZE_METERS = 0.1;
/** 预览面相对模型包围盒对角线的放大系数。 */
const PLANE_VISUAL_SIZE_SCALE = 1.25;
/** 剖切面预览透明度。 */
const SECTION_PREVIEW_OPACITY = 0.2;
/** 单个盖面子阶段内，stencil 代理必须先于盖面提交。 */
const SECTION_STENCIL_RENDER_ORDER = 0;
/** 单个盖面子阶段内，盖面必须在 stencil 计数完成后提交。 */
const SECTION_CAP_RENDER_ORDER = 1;
/** 剖切面预览位于全部盖面之后。 */
const SECTION_PREVIEW_RENDER_ORDER_BASE = 20_000;
/** 平面法向合法性阈值。 */
const MINIMUM_PLANE_NORMAL_LENGTH_SQUARED = 1e-12;
/** 默认剖切颜色；调用方仍应传入与剖面一一对应的颜色。 */
const DEFAULT_SECTION_COLORS = ['#f4df4e', '#72b7ff', '#ef8b8b'] as const;

/**
 * 剖面渲染辅助对象不参与实体拾取。
 *
 * @returns 无返回值；该空实现是 Three.js raycast 协议的显式禁用标记。
 */
function ignoreSectionViewHelperRaycast(): void {
    // 剖切预览与 stencil 代理仅承担渲染职责，不能污染 CAD 实体选择结果。
}

/**
 * 判断场景对象在指定模型根内是否有效可见。
 *
 * @param object 待判断对象。
 * @param modelRoot 可剖切模型根。
 * @returns 对象及其祖先在模型根内均可见时返回 true。
 */
function isObjectHierarchyVisible(object: THREE.Object3D, modelRoot: THREE.Object3D): boolean {
    let currentObject: THREE.Object3D | null = object;
    while (currentObject) {
        if (!currentObject.visible) {
            return false;
        }
        if (currentObject === modelRoot) {
            return true;
        }
        currentObject = currentObject.parent;
    }
    return false;
}

/**
 * 判断网格是否适合参与基础 stencil 剖面盖。
 *
 * @param object 模型根中的场景对象。
 * @param modelRoot 可剖切模型根。
 * @returns 静态、可见、写颜色且非 FeatureFace 语义代理的三角网格返回 true。
 */
function isSectionCapSourceMesh(
    object: THREE.Object3D,
    modelRoot: THREE.Object3D,
): object is THREE.Mesh {
    if (!(object instanceof THREE.Mesh)) {
        return false;
    }
    if (object instanceof THREE.InstancedMesh || object instanceof THREE.SkinnedMesh) {
        return false;
    }
    if (isFeatureFace(object)) {
        // FeatureFace 复用结果体表面；重复写 stencil 会破坏奇偶计数。
        return false;
    }
    if (!isObjectHierarchyVisible(object, modelRoot)) {
        return false;
    }
    if (!object.geometry.getAttribute('position')) {
        return false;
    }

    const materials = Array.isArray(object.material) ? object.material : [object.material];
    return materials.some((material) => (
        material.visible
        && material.colorWrite
        && material.opacity > 0
    ));
}

/**
 * WebGPU 剖面视图渲染控制器。
 *
 * 职责与边界：
 * 1. 只管理 ClippingGroup、剖切面预览和基础 stencil 剖面盖，不持有业务草稿或持久化状态；
 * 2. 三套平面视觉、材质和 stencil 材质在构造时一次创建，拖拽更新只修改已有对象；
 * 3. stencil 代理共享源模型 geometry，模型内容变化时才重建，dispose 时绝不释放源模型资源；
 * 4. 预览留在主场景，三套盖面分别位于独立稳定 Scene，由主场景 Pass 建立真实 stencil 边界；
 * 5. 调用方必须在移除 ClippingGroup 前调用 `dispose()`，以释放辅助几何体、材质和场景引用。
 */
export class SectionViewRenderController implements
    SectionViewRenderPort,
    SectionViewCapRenderStageSource {
    /** 承载全部可剖切模型的 WebGPU 局部裁剪根。 */
    private readonly clippingRoot: THREE.ClippingGroup;
    /** 主场景中只承载半透明剖切面预览的运行时根。 */
    private readonly helperRoot = new THREE.Group();
    /** 固定三套剖面盖子阶段场景；每套拥有独立 stencil 清零边界。 */
    private readonly capRenderStageScenes: THREE.Scene[] = [];
    /** 单位剖切面几何体，三套预览与盖面共同复用。 */
    private readonly planeGeometry = new THREE.PlaneGeometry(1, 1);
    /** 固定三套半透明预览面。 */
    private readonly previewMeshes: THREE.Mesh[] = [];
    /** 固定三套预览材质。 */
    private readonly previewMaterials: THREE.MeshBasicMaterial[] = [];
    /** 固定三套剖面盖裁剪组，仅承载“其它活动剖面”。 */
    private readonly capClippingGroups: THREE.ClippingGroup[] = [];
    /** 固定三套剖面盖。 */
    private readonly capMeshes: THREE.Mesh[] = [];
    /** 固定三套剖面盖材质。 */
    private readonly capMaterials: THREE.MeshBasicMaterial[] = [];
    /** 固定三套 stencil 模型裁剪组。 */
    private readonly stencilClippingGroups: THREE.ClippingGroup[] = [];
    /** 固定三套背面递增 stencil 材质。 */
    private readonly stencilBackMaterials: THREE.MeshBasicMaterial[] = [];
    /** 固定三套正面递减 stencil 材质。 */
    private readonly stencilFrontMaterials: THREE.MeshBasicMaterial[] = [];
    /** 当前活动剖切面引用；数组本身始终复用。 */
    private readonly activePlanes: THREE.Plane[] = [];
    /** 当前活动颜色；Color 实例始终复用。 */
    private readonly activeColors = [new THREE.Color(), new THREE.Color(), new THREE.Color()];
    /** 模型世界空间包围盒。 */
    private readonly modelBounds = new THREE.Box3();
    /** 包围盒中心计算缓存。 */
    private readonly modelBoundsCenter = new THREE.Vector3();
    /** 包围盒尺寸计算缓存。 */
    private readonly modelBoundsSize = new THREE.Vector3();
    /** 平面视觉中心计算缓存。 */
    private readonly planeVisualCenter = new THREE.Vector3();
    /** 平面视觉法向计算缓存。 */
    private readonly planeNormal = new THREE.Vector3();
    /** PlaneGeometry 默认法向。 */
    private readonly defaultPlaneNormal = new THREE.Vector3(0, 0, 1);
    /** 预览是否可见。 */
    private isPreviewVisible = false;
    /** 基础剖面盖是否启用。 */
    private isCapEnabled = false;
    /** stencil 代理是否需要按最新模型内容重建。 */
    private areStencilProxiesDirty = true;
    /** 当前平面视觉边长。 */
    private planeVisualSize = MINIMUM_PLANE_VISUAL_SIZE_METERS;
    /** 生命周期是否已经结束。 */
    private isDisposed = false;

    /**
     * 创建剖面视图渲染控制器。
     *
     * @param clippingRoot 已加入主场景、并承载可剖切模型的 ClippingGroup。
     * @throws 入参不是 ClippingGroup，或裁剪根尚未加入场景时抛出错误。
     */
    public constructor(clippingRoot: THREE.ClippingGroup) {
        if (clippingRoot.isClippingGroup !== true) {
            throw new Error('剖面视图渲染根必须是 THREE.ClippingGroup');
        }
        if (!clippingRoot.parent) {
            throw new Error('剖面视图 ClippingGroup 必须先加入主场景再创建渲染控制器');
        }

        this.clippingRoot = clippingRoot;
        // ClippingGroup 只是局部裁剪载体，离线快照应折叠它但继续导出其真实模型子树。
        markCadObjectAsRuntimeContainer(this.clippingRoot);
        this.clippingRoot.enabled = false;
        // Three 的 union plane discard 会裁掉任一负半空间，从而保留全部正半空间的交集。
        this.clippingRoot.clipIntersection = false;
        this.clippingRoot.clipShadows = true;
        this.helperRoot.name = '__section_view_render_helpers__';
        // 只标记预览根；clippingRoot 内是真实模型，必须保留组件语义。
        SceneAuxiliaryObjectRegistry.markRoot(this.helperRoot);
        // 主场景只挂可由剖面定义重建的预览面，不得进入模型快照。
        markCadObjectAsRuntimeDerived(this.helperRoot);
        clippingRoot.parent.add(this.helperRoot);

        this.createStablePlaneResources();
        this.refreshModelBounds();
        this.refreshPlaneVisuals();
    }

    /**
     * 获取当前需要提交的盖面子阶段数量。
     *
     * @returns 盖面启用时返回活动剖面数量，否则返回 0。
     * @throws 控制器已释放时抛出错误。
     */
    public getActiveCapRenderStageCount(): number {
        this.assertActive();
        return this.isCapEnabled ? this.activePlanes.length : 0;
    }

    /**
     * 读取指定活动剖面的稳定盖面子阶段场景。
     *
     * @param stageIndex 零基活动剖面索引。
     * @returns 只包含目标剖面 stencil 代理与盖面的 Scene。
     * @throws 控制器已释放或索引不属于当前活动盖面范围时抛出错误。
     */
    public getCapRenderStageScene(stageIndex: number): THREE.Scene {
        this.assertActive();
        const activeStageCount = this.getActiveCapRenderStageCount();
        if (
            !Number.isInteger(stageIndex)
            || stageIndex < 0
            || stageIndex >= activeStageCount
        ) {
            throw new Error(
                `剖面盖子阶段索引越界: index=${stageIndex}, count=${activeStageCount}`,
            );
        }
        return this.capRenderStageScenes[stageIndex];
    }

    /**
     * 更新当前参与裁剪的数学平面及对应颜色。
     *
     * @param planes 按剖面槽位顺序排列的 0 至 3 个稳定 SectionPlane。
     * @param colors 与剖切面一一对应的 CSS 颜色。
     * @returns 无返回值。
     * @throws 平面数量、颜色数量或平面数学数据不合法时抛出错误。
     */
    public setPlanes(planes: readonly SectionPlane[], colors: readonly string[]): void {
        this.assertActive();
        this.assertPlaneInputs(planes, colors);

        const previousPlaneCount = this.activePlanes.length;
        this.activePlanes.length = 0;
        this.clippingRoot.clippingPlanes.length = 0;
        for (let planeIndex = 0; planeIndex < planes.length; planeIndex += 1) {
            const plane = planes[planeIndex];
            this.activePlanes.push(plane);
            this.clippingRoot.clippingPlanes.push(plane);
            this.activeColors[planeIndex].set(colors[planeIndex]);
        }

        if (previousPlaneCount !== this.activePlanes.length) {
            // 代理只为活动子阶段创建；增减剖面时必须同步增删对应代理，但拖动同数量平面无需重建。
            this.areStencilProxiesDirty = true;
        }

        this.clippingRoot.enabled = this.activePlanes.length > 0;
        this.configureHelperClippingGroups();
        this.refreshPlaneVisuals();
        this.ensureStencilProxies();
    }

    /**
     * 设置剖切参考面的编辑预览可见性。
     *
     * @param visible 是否显示半透明剖切面。
     * @returns 无返回值。
     * @throws 控制器已释放时抛出错误。
     */
    public setPreviewVisible(visible: boolean): void {
        this.assertActive();
        this.isPreviewVisible = visible;
        this.refreshHelperVisibility();
    }

    /**
     * 设置基础 stencil 剖面盖是否启用。
     *
     * @param enabled 是否显示剖面盖。
     * @returns 无返回值。
     * @throws 控制器已释放时抛出错误。
     */
    public setCapEnabled(enabled: boolean): void {
        this.assertActive();
        this.isCapEnabled = enabled;
        if (enabled) {
            this.ensureStencilProxies();
        } else {
            // 关闭盖面时立即解除对源 geometry 的共享引用，避免无效代理长期占用模型资源。
            this.clearStencilProxies();
        }
        this.refreshHelperVisibility();
    }

    /**
     * 通知可剖切模型内容、可见性或静态几何发生变化。
     *
     * 模型增删时才重建 stencil 代理；平面拖拽只调用 setPlanes，不进入本路径。
     *
     * @returns 无返回值。
     * @throws 控制器已释放时抛出错误。
     */
    public notifyContentChanged(): void {
        this.assertActive();
        this.refreshModelBounds();
        this.refreshPlaneVisuals();
        this.areStencilProxiesDirty = true;

        if (this.isCapEnabled && this.activePlanes.length > 0) {
            this.rebuildStencilProxies();
        } else {
            // 盖面关闭期间立即解除旧源 geometry 引用，避免模型卸载后代理继续保留已释放资源。
            this.clearStencilProxies();
        }
    }

    /**
     * 释放全部剖面渲染资源并恢复完整模型显示。
     *
     * 本方法可重入；只释放控制器自建资源，不释放 ClippingGroup 或源模型 geometry。
     *
     * @returns 无返回值。
     */
    public dispose(): void {
        if (this.isDisposed) {
            return;
        }
        this.isDisposed = true;

        this.clearStencilProxies();
        this.clippingRoot.clippingPlanes.length = 0;
        this.clippingRoot.enabled = false;

        if (this.helperRoot.parent) {
            this.helperRoot.parent.remove(this.helperRoot);
        }
        this.helperRoot.clear();
        this.capRenderStageScenes.forEach((stageScene) => stageScene.clear());

        this.previewMaterials.forEach((material) => material.dispose());
        this.capMaterials.forEach((material) => material.dispose());
        this.stencilBackMaterials.forEach((material) => material.dispose());
        this.stencilFrontMaterials.forEach((material) => material.dispose());
        this.planeGeometry.dispose();

        this.capRenderStageScenes.length = 0;
        this.previewMeshes.length = 0;
        this.previewMaterials.length = 0;
        this.capClippingGroups.length = 0;
        this.capMeshes.length = 0;
        this.capMaterials.length = 0;
        this.stencilClippingGroups.length = 0;
        this.stencilBackMaterials.length = 0;
        this.stencilFrontMaterials.length = 0;
        this.activePlanes.length = 0;
    }

    /**
     * 一次创建固定三套预览、盖面与 stencil 材质。
     *
     * @returns 无返回值。
     */
    private createStablePlaneResources(): void {
        for (let planeIndex = 0; planeIndex < MAXIMUM_SECTION_PLANE_COUNT; planeIndex += 1) {
            const capRenderStageScene = new THREE.Scene();
            capRenderStageScene.name = `__section_view_cap_render_stage_${planeIndex}__`;
            this.capRenderStageScenes.push(capRenderStageScene);

            const previewMaterial = this.createPreviewMaterial(DEFAULT_SECTION_COLORS[planeIndex]);
            const previewMesh = new THREE.Mesh(this.planeGeometry, previewMaterial);
            previewMesh.name = `__section_view_preview_plane_${planeIndex}__`;
            previewMesh.renderOrder = SECTION_PREVIEW_RENDER_ORDER_BASE + planeIndex;
            previewMesh.raycast = ignoreSectionViewHelperRaycast;
            this.previewMaterials.push(previewMaterial);
            this.previewMeshes.push(previewMesh);
            this.helperRoot.add(previewMesh);

            const capClippingGroup = new THREE.ClippingGroup();
            capClippingGroup.name = `__section_view_cap_clipping_group_${planeIndex}__`;
            capClippingGroup.clipIntersection = false;
            const capMaterial = this.createCapMaterial(DEFAULT_SECTION_COLORS[planeIndex]);
            const capMesh = new THREE.Mesh(this.planeGeometry, capMaterial);
            capMesh.name = `__section_view_cap_plane_${planeIndex}__`;
            // 盖面不参与视锥裁剪，避免接近视口边界时因薄平面包围盒出现闪断。
            capMesh.frustumCulled = false;
            capMesh.renderOrder = SECTION_CAP_RENDER_ORDER;
            capMesh.raycast = ignoreSectionViewHelperRaycast;
            capClippingGroup.add(capMesh);
            this.capClippingGroups.push(capClippingGroup);
            this.capMaterials.push(capMaterial);
            this.capMeshes.push(capMesh);
            capRenderStageScene.add(capClippingGroup);

            const stencilClippingGroup = new THREE.ClippingGroup();
            stencilClippingGroup.name = `__section_view_stencil_clipping_group_${planeIndex}__`;
            stencilClippingGroup.clipIntersection = false;
            this.stencilClippingGroups.push(stencilClippingGroup);
            capRenderStageScene.add(stencilClippingGroup);

            const stencilBackMaterial = this.createStencilMaterial(THREE.BackSide, true);
            this.stencilBackMaterials.push(stencilBackMaterial);

            const stencilFrontMaterial = this.createStencilMaterial(THREE.FrontSide, false);
            this.stencilFrontMaterials.push(stencilFrontMaterial);
        }
    }

    /**
     * 创建半透明剖切面预览材质。
     *
     * @param color 初始颜色。
     * @returns 可复用预览材质。
     */
    private createPreviewMaterial(color: string): THREE.MeshBasicMaterial {
        return new THREE.MeshBasicMaterial({
            color,
            transparent: true,
            opacity: SECTION_PREVIEW_OPACITY,
            side: THREE.DoubleSide,
            depthWrite: false,
            toneMapped: false,
            polygonOffset: true,
            polygonOffsetFactor: -1,
            polygonOffsetUnits: -1,
        });
    }

    /**
     * 创建由 stencil 非零区域约束的基础剖面盖材质。
     *
     * @param color 初始颜色。
     * @returns 可复用盖面材质。
     */
    private createCapMaterial(color: string): THREE.MeshBasicMaterial {
        const material = new THREE.MeshBasicMaterial({
            color,
            side: THREE.DoubleSide,
            toneMapped: false,
            polygonOffset: true,
            polygonOffsetFactor: -1,
            polygonOffsetUnits: -1,
        });
        material.stencilWrite = true;
        material.stencilRef = 0;
        material.stencilFunc = THREE.NotEqualStencilFunc;
        material.stencilFail = THREE.ReplaceStencilOp;
        material.stencilZFail = THREE.ReplaceStencilOp;
        material.stencilZPass = THREE.ReplaceStencilOp;
        return material;
    }

    /**
     * 创建基础剖面盖的正面或背面 stencil 材质。
     *
     * @param side 绘制面方向。
     * @param shouldIncrement 通过深度时是否递增 stencil；false 表示递减。
     * @returns 可复用 stencil 材质。
     */
    private createStencilMaterial(
        side: THREE.Side,
        shouldIncrement: boolean,
    ): THREE.MeshBasicMaterial {
        const material = new THREE.MeshBasicMaterial({ side });
        material.depthWrite = false;
        material.depthTest = false;
        material.colorWrite = false;
        material.stencilWrite = true;
        material.stencilFunc = THREE.AlwaysStencilFunc;
        material.stencilFail = shouldIncrement
            ? THREE.IncrementWrapStencilOp
            : THREE.DecrementWrapStencilOp;
        material.stencilZFail = shouldIncrement
            ? THREE.IncrementWrapStencilOp
            : THREE.DecrementWrapStencilOp;
        material.stencilZPass = shouldIncrement
            ? THREE.IncrementWrapStencilOp
            : THREE.DecrementWrapStencilOp;
        return material;
    }

    /**
     * 同步主裁剪面到三套 stencil 组，并让盖面只受其它剖切面限制。
     *
     * @returns 无返回值。
     */
    private configureHelperClippingGroups(): void {
        for (let planeIndex = 0; planeIndex < MAXIMUM_SECTION_PLANE_COUNT; planeIndex += 1) {
            const stencilClippingGroup = this.stencilClippingGroups[planeIndex];
            stencilClippingGroup.clippingPlanes.length = 0;
            const targetPlane = this.activePlanes[planeIndex];
            if (targetPlane) {
                // 每套 winding 计数只针对自身剖面，避免其它剖面提前把代理裁成开放体。
                stencilClippingGroup.clippingPlanes.push(targetPlane);
            }
            stencilClippingGroup.enabled = targetPlane !== undefined;

            const capClippingGroup = this.capClippingGroups[planeIndex];
            capClippingGroup.clippingPlanes.length = 0;
            for (let otherPlaneIndex = 0; otherPlaneIndex < this.activePlanes.length; otherPlaneIndex += 1) {
                if (otherPlaneIndex !== planeIndex) {
                    capClippingGroup.clippingPlanes.push(this.activePlanes[otherPlaneIndex]);
                }
            }
            capClippingGroup.enabled = capClippingGroup.clippingPlanes.length > 0;
        }
        this.refreshHelperVisibility();
    }

    /**
     * 更新固定三套预览与盖面的颜色、位置、方向和尺寸。
     *
     * @returns 无返回值。
     */
    private refreshPlaneVisuals(): void {
        for (let planeIndex = 0; planeIndex < MAXIMUM_SECTION_PLANE_COUNT; planeIndex += 1) {
            const plane = this.activePlanes[planeIndex];
            if (!plane) {
                continue;
            }

            this.planeNormal.copy(plane.normal).normalize();
            plane.projectPoint(this.modelBoundsCenter, this.planeVisualCenter);

            const previewMesh = this.previewMeshes[planeIndex];
            const capMesh = this.capMeshes[planeIndex];
            previewMesh.position.copy(this.planeVisualCenter);
            capMesh.position.copy(this.planeVisualCenter);
            previewMesh.quaternion.setFromUnitVectors(this.defaultPlaneNormal, this.planeNormal);
            capMesh.quaternion.copy(previewMesh.quaternion);
            previewMesh.scale.setScalar(this.planeVisualSize);
            capMesh.scale.copy(previewMesh.scale);
            previewMesh.updateMatrix();
            capMesh.updateMatrix();

            this.previewMaterials[planeIndex].color.copy(this.activeColors[planeIndex]);
            this.capMaterials[planeIndex].color.copy(this.activeColors[planeIndex]);
        }
        this.refreshHelperVisibility();
    }

    /**
     * 根据当前活动平面、预览开关和盖面开关同步辅助对象可见性。
     *
     * @returns 无返回值。
     */
    private refreshHelperVisibility(): void {
        for (let planeIndex = 0; planeIndex < MAXIMUM_SECTION_PLANE_COUNT; planeIndex += 1) {
            const hasActivePlane = planeIndex < this.activePlanes.length;
            this.previewMeshes[planeIndex].visible = hasActivePlane && this.isPreviewVisible;
            this.capClippingGroups[planeIndex].visible = hasActivePlane && this.isCapEnabled;
            this.stencilClippingGroups[planeIndex].visible = hasActivePlane && this.isCapEnabled;
        }
    }

    /**
     * 重算模型世界包围盒和预览面稳定尺寸。
     *
     * @returns 无返回值。
     */
    private refreshModelBounds(): void {
        this.clippingRoot.updateWorldMatrix(true, true);
        this.modelBounds.setFromObject(this.clippingRoot, true);
        if (this.modelBounds.isEmpty()) {
            this.modelBoundsCenter.set(0, 0, 0);
            this.modelBoundsSize.setScalar(MINIMUM_PLANE_VISUAL_SIZE_METERS);
            this.planeVisualSize = MINIMUM_PLANE_VISUAL_SIZE_METERS;
            return;
        }

        this.modelBounds.getCenter(this.modelBoundsCenter);
        this.modelBounds.getSize(this.modelBoundsSize);
        this.planeVisualSize = Math.max(
            this.modelBoundsSize.length() * PLANE_VISUAL_SIZE_SCALE,
            MINIMUM_PLANE_VISUAL_SIZE_METERS,
        );
    }

    /**
     * 盖面开启且模型代理过期时，按最新模型内容重建一次 stencil 代理。
     *
     * @returns 无返回值。
     */
    private ensureStencilProxies(): void {
        if (!this.isCapEnabled) {
            return;
        }
        if (this.activePlanes.length === 0) {
            // 没有活动剖面时立即解除源 geometry 引用，避免不可见代理滞留到下一次会话。
            this.clearStencilProxies();
            return;
        }
        if (!this.areStencilProxiesDirty) {
            return;
        }
        this.rebuildStencilProxies();
    }

    /**
     * 为每套剖面构建共享源 geometry 的正反面 stencil 代理。
     *
     * FeatureFace 等明确的重复表面代理不作为独立源；普通 Mesh-in-Mesh 仍按真实模型层级处理。
     *
     * @returns 无返回值。
     */
    private rebuildStencilProxies(): void {
        this.clearStencilProxies();
        const sourceMeshes: THREE.Mesh[] = [];
        this.clippingRoot.traverse((object) => {
            if (isSectionCapSourceMesh(object, this.clippingRoot)) {
                sourceMeshes.push(object);
            }
        });

        for (let planeIndex = 0; planeIndex < this.activePlanes.length; planeIndex += 1) {
            const stencilGroup = this.stencilClippingGroups[planeIndex];

            sourceMeshes.forEach((sourceMesh) => {
                const backStencilMesh = this.createStencilProxy(
                    sourceMesh,
                    this.stencilBackMaterials[planeIndex],
                    SECTION_STENCIL_RENDER_ORDER,
                );
                const frontStencilMesh = this.createStencilProxy(
                    sourceMesh,
                    this.stencilFrontMaterials[planeIndex],
                    SECTION_STENCIL_RENDER_ORDER,
                );
                stencilGroup.add(backStencilMesh, frontStencilMesh);
            });
        }
        this.areStencilProxiesDirty = false;
    }

    /**
     * 创建与源静态网格共用 geometry 的 stencil 代理。
     *
     * @param sourceMesh 源模型网格。
     * @param material 共享 stencil 材质。
     * @param renderOrder 当前剖面的渲染顺序。
     * @returns 不参与拾取且按源矩阵更新的代理网格。
     */
    private createStencilProxy(
        sourceMesh: THREE.Mesh,
        material: THREE.MeshBasicMaterial,
        renderOrder: number,
    ): THREE.Mesh {
        const proxyMesh = new THREE.Mesh(sourceMesh.geometry, material);
        proxyMesh.name = '__section_view_stencil_proxy__';
        proxyMesh.matrixAutoUpdate = false;
        proxyMesh.matrixWorldAutoUpdate = false;
        proxyMesh.frustumCulled = false;
        proxyMesh.renderOrder = renderOrder;
        proxyMesh.raycast = ignoreSectionViewHelperRaycast;
        proxyMesh.matrixWorld.copy(sourceMesh.matrixWorld);
        // 源对象可能在不重建几何的装配拖拽中移动；每次绘制只复制复用矩阵，不创建临时对象。
        proxyMesh.onBeforeRender = () => {
            proxyMesh.matrixWorld.copy(sourceMesh.matrixWorld);
        };
        return proxyMesh;
    }

    /**
     * 移除全部 stencil 代理并解除源模型 geometry 引用。
     *
     * 代理共享源 geometry，因此这里只移除 Object3D，绝不调用 geometry.dispose()。
     *
     * @returns 无返回值。
     */
    private clearStencilProxies(): void {
        this.stencilClippingGroups.forEach((stencilGroup) => {
            stencilGroup.children.forEach((child) => {
                child.onBeforeRender = ignoreSectionViewHelperRaycast;
            });
            stencilGroup.clear();
        });
        this.areStencilProxiesDirty = true;
    }

    /**
     * 校验外部传入的剖切平面与颜色数组。
     *
     * @param planes 当前剖切面。
     * @param colors 对应颜色。
     * @returns 无返回值。
     * @throws 数量、平面数学数据或颜色输入不合法时抛出错误。
     */
    private assertPlaneInputs(planes: readonly SectionPlane[], colors: readonly string[]): void {
        if (planes.length > MAXIMUM_SECTION_PLANE_COUNT) {
            throw new Error(`剖面视图最多支持 ${MAXIMUM_SECTION_PLANE_COUNT} 个活动剖切面`);
        }
        if (colors.length !== planes.length) {
            throw new Error(`剖切面与颜色数量不一致: planes=${planes.length}, colors=${colors.length}`);
        }

        planes.forEach((plane, planeIndex) => {
            if (plane.isPlane !== true) {
                throw new Error(`剖面视图收到非法平面对象: index=${planeIndex}`);
            }
            if (
                !Number.isFinite(plane.normal.x)
                || !Number.isFinite(plane.normal.y)
                || !Number.isFinite(plane.normal.z)
                || !Number.isFinite(plane.constant)
                || plane.normal.lengthSq() <= MINIMUM_PLANE_NORMAL_LENGTH_SQUARED
            ) {
                throw new Error(`剖面视图收到非法平面数学数据: index=${planeIndex}`);
            }
            if (colors[planeIndex].trim().length === 0) {
                throw new Error(`剖面视图颜色不能为空: index=${planeIndex}`);
            }
        });
    }

    /**
     * 断言控制器尚未释放。
     *
     * @returns 无返回值。
     * @throws 控制器生命周期已结束时抛出错误。
     */
    private assertActive(): void {
        if (this.isDisposed) {
            throw new Error('剖面视图渲染控制器已释放');
        }
    }
}
